import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
  TrackByFunction,
} from '@angular/core';
import { FormlyModule } from '@ngx-formly/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { SvgService } from '../../services/svg.services';
import { FormlyErrorComponent } from '../formly-error.component';
import { startWith, tap } from 'rxjs/operators';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { takeUntil } from 'rxjs/operators';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { ClearableFieldType } from './clearable-field.type';
import { FieldLabelRowComponent } from './field-label-row.component';
import { commitUserValue } from './user-edit.util';

interface IconOption {
  value: string; // The value to be saved (e.g. "heroicons_solid:user")
  displayName: string; // User-friendly name (e.g. "user")
  category: string; // The icon set name (e.g. "Heroicons Solid")
  isCustom?: boolean; // Whether this is a custom icon
}

@Component({
  selector: 'formly-field-icon-picker',
  standalone: true,
  template: `
    <!--
      Width classes from field.className are already applied by formly to the host
      <formly-field>. Re-applying them here compounded them (a half-width field rendered at
      50% of 50%); the inner field just fills its host.
    -->
    <mat-form-field class="w-full" [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
      <mat-label>
        <app-field-label-row
          [label]="props.label + (field.props.required ? ' *' : '')"
          [required]="field.props.required"></app-field-label-row>
      </mat-label>

      <!-- Selected icon preview -->
      @if (formControl.value) {
        <mat-icon matPrefix class="icon-size-5 mr-2" [svgIcon]="formControl.value"></mat-icon>
      }

      <!-- Autocomplete input -->
      <input
        #searchInput
        matInput
        type="text"
        [formControl]="autocompleteControl"
        [matAutocomplete]="auto"
        [formlyDataCy]="field"
        [placeholder]="'Zoek een icon'" />

      <app-clear-field-button
        matSuffix
        [field]="field"
        [hasValue]="hasClearableValue() || !!autocompleteControl.value"
        [disabled]="isDisabled()"
        [readOnly]="isReadOnly()"
        [ariaLabel]="clearAriaLabel()"
        [focusTarget]="searchInput"
        (clearSelection)="clearSelection()" />

      <!-- Autocomplete panel -->
      <mat-autocomplete
        #auto="matAutocomplete"
        [displayWith]="displayIconName"
        (optionSelected)="onIconSelected($event)">
        <!-- No results message -->
        @if (filteredOptions().length === 0 && searchTerm()) {
          <mat-option disabled> Geen resultaten gevonden voor "{{ searchTerm() }}" </mat-option>
        }

        <!-- Group headers and icons -->
        @for (group of groupedFilteredOptions(); track group.name) {
          <!-- Only show groups with options -->
          @if (group.options.length > 0) {
            <!-- Group header -->
            <mat-option disabled class="group-header"> {{ group.name }} ({{ group.options.length }}) </mat-option>

            <!-- Icons in this group -->
            @for (option of group.options; track trackByOption($index, option)) {
              <mat-option [value]="option">
                <div class="flex items-center gap-2">
                  <mat-icon class="icon-size-5" [svgIcon]="option.value"></mat-icon>
                  <span>{{ option.displayName }}</span>
                </div>
              </mat-option>
            }
          }
        }
      </mat-autocomplete>

      <!-- Hint and error messages -->
      @if (field.props.description) {
        <mat-hint>{{ field.props.description }}</mat-hint>
      }
      <app-formly-error [field]="field"></app-formly-error>
    </mat-form-field>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatIcon,
    MatAutocompleteModule,
    MatButtonModule,
    FormlyModule,
    FormlyErrorComponent,
    FormlyDataCyDirective,
    ClearFieldButtonComponent,
    FieldLabelRowComponent,
  ],
  providers: [SvgService],
  styles: `
    ::ng-deep {
      .mat-mdc-form-field-icon-prefix > .mat-icon,
      .mat-mdc-form-field-icon-suffix > .mat-icon {
        padding: 4px;
      }

      .group-header {
        font-weight: bold;
        background-color: rgba(0, 0, 0, 0.04);
        color: rgba(0, 0, 0, 0.87) !important;
        font-size: 14px;
        letter-spacing: 0.5px;
      }

      .mat-mdc-option {
        min-height: 40px;
        line-height: 40px;
      }

      .mat-mdc-option.mat-mdc-option-active {
        background: rgba(0, 0, 0, 0.04);
      }

      .mat-mdc-autocomplete-panel {
        max-height: 70vh !important;
      }
    }
  `,
})
export class FormlyFieldIconPickerComponent extends ClearableFieldType implements OnInit {
  svgService = inject(SvgService);

  // Form controls
  autocompleteControl = new FormControl('');
  searchTerm = signal('');

  // Track all available icons
  allIcons = signal<IconOption[]>([]);

  // Inject destroy$ for clean subscription handling
  private destroy$ = injectDestroy();

  // Transform original icon sets into options format
  @ViewChild('auto') autocomplete: any;

  // Filtered options based on search term
  filteredOptions = computed(() => {
    const term = this.searchTerm().toLowerCase();
    if (!term) return this.allIcons();

    return this.allIcons().filter(
      option => option.displayName.toLowerCase().includes(term) || option.category.toLowerCase().includes(term)
    );
  });

  // Group filtered options by category
  groupedFilteredOptions = computed(() => {
    const options = this.filteredOptions();
    const groups: { name: string; options: IconOption[] }[] = [];

    // Group by category
    options.forEach(option => {
      const existingGroup = groups.find(g => g.name === option.category);
      if (existingGroup) {
        existingGroup.options.push(option);
      } else {
        groups.push({
          name: option.category,
          options: [option],
        });
      }
    });

    return groups;
  });
  // TrackBy function for better ngFor performance
  trackByOption: TrackByFunction<any> = (index: number, option: any) => {
    return option.value || index;
  };

  /** Keeps the local search control's enabled state in step with the Formly field's own. */
  private syncDisabledState(): void {
    const disabled = this.isDisabled();
    if (disabled === this.autocompleteControl.disabled) return;
    // emitEvent: false — this mirrors state, it is not a user edit, and the valueChanges
    // subscription above would otherwise re-run the search on a pure enable/disable.
    if (disabled) this.autocompleteControl.disable({ emitEvent: false });
    else this.autocompleteControl.enable({ emitEvent: false });
  }

  ngOnInit(): void {
    // Load icons when the component initializes
    this.svgService.loadSvgIcons();

    // Convert icon sets to options
    this.initializeIconOptions();

    // Link autocomplete to actual form control
    this.syncFormControlValues();

    // The visible input is bound to autocompleteControl, a LOCAL control — disabling the field
    // disables Formly's control, which this one never hears about, so a disabled icon picker
    // still accepted typing (caught by the disabled invariant on the new Velden/Custom/Icoon
    // story). Mirror the state on init and on every later change.
    this.syncDisabledState();
    this.formControl.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncDisabledState());

    // Subscribe to search input changes
    this.autocompleteControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(''),
        tap(value => {
          // If the input value is a string, it's search text
          if (typeof value === 'string') {
            this.searchTerm.set(value);
          }
        })
      )
      .subscribe();
  }

  // Initialize all icon options from the service
  private initializeIconOptions(): void {
    // Wait for icons to be loaded
    setTimeout(() => {
      const iconOptions: IconOption[] = [];

      // Add standard icon sets
      this.svgService.icons().forEach(iconSet => {
        iconSet.icons.forEach(iconName => {
          const value = iconSet.registerName ? `${iconSet.registerName}:${iconName}` : iconName;
          iconOptions.push({
            value,
            displayName: iconName,
            category: iconSet.name,
          });
        });
      });

      // Add custom icons
      this.svgService.customIcons.forEach(icon => {
        iconOptions.push({
          value: icon.iconId,
          displayName: icon.name,
          category: 'Custom Icons',
          isCustom: true,
        });
      });

      // Update signal
      this.allIcons.set(iconOptions);

      // If there's a value already, set the autocomplete input
      if (this.formControl.value) {
        const iconOption = this.findIconOptionByValue(this.formControl.value);
        if (iconOption) {
          this.autocompleteControl.setValue(iconOption.value);
        }
      }
    }, 100);
  }

  // Find icon option by its value
  private findIconOptionByValue(value: string): IconOption | undefined {
    return this.allIcons().find(option => option.value === value);
  }

  // Sync the autocomplete control with the form control
  private syncFormControlValues(): void {
    // When the main form control changes, update autocomplete
    this.formControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap(value => {
          if (value && typeof value === 'string') {
            const iconOption = this.findIconOptionByValue(value);
            if (iconOption && this.autocompleteControl.value !== iconOption.value) {
              this.autocompleteControl.setValue(iconOption.value);
            }
          } else if (!value) {
            this.autocompleteControl.setValue('');
          }
        })
      )
      .subscribe();
  }

  // Display function for autocomplete
  displayIconName = (option: IconOption | string): string => {
    // If it's a string, return it (happens during search)
    if (!option || typeof option === 'string') {
      return option as string;
    }

    // Otherwise display the icon name
    return option.displayName;
  };

  // Handle selection from autocomplete
  onIconSelected(event: MatAutocompleteSelectedEvent): void {
    const iconOption = event.option.value as IconOption;
    commitUserValue(this.formControl, iconOption.value);
  }

  /**
   * The search control mirrors the selection and is UI-only state, so a plain reset suffices
   * there; the value control goes through the shared contract so the entry save sees the wipe
   * (null + dirty + touched — a bare setValue('') was invisible to dirty-tracking).
   */
  clearSelection(): void {
    this.autocompleteControl.setValue('');
    this.clear();
  }
}
